/**
 * Generic fallback scorer.
 *
 * Always-present last resort covering the single-availability-column,
 * explicit-date-range (non-OMI), and implicit-offer-is-free families. Never
 * returns `null` — this is the guaranteed non-null tail of
 * `resolveAvailabilityProfile`.
 *
 * Traceability: availability-profile-resolution / Requirement: Generic
 * fallback scorer.
 */
import type {
  AvailabilityProfile,
  AvailabilityTimeEncoding,
} from '../../schemas/availability-profile.js';
import type { AvailabilityProfileResolver, AvailabilityProfileResolveResult } from './types.js';
import { findHeaderAmong, normalizeHeaderForMatch } from './header-match.js';
import {
  CANONICAL_BUSY_TOKENS,
  CANONICAL_FREE_TOKENS,
  isBusyToken,
  normalizeToken,
} from './status-normalization.js';
import { buildAvailabilityProfileSkeleton } from './profile-skeleton.js';

/** A candidate period-label column must have at least this many distinct values... */
const MIN_PERIOD_LABEL_DISTINCT_VALUES = 2;
/** ...and no more than this many — beyond this it reads as free-form notes, not a bounded vocabulary. */
const MAX_PERIOD_LABEL_DISTINCT_VALUES = 20;

const GRW_ID_HEADERS = ['grw_id', 'grw id', 'id grw'];
// Aliases beyond the canonical 'numer dostawcy' come from real supplier files
// on CU-86canaqmw: 'symbol' (CS_10-11.xlsx / citisign), 'nr'
// (Outdoor3miasto_pomorska.xlsx), 'nr tablicy' (Bridge42_bb.xlsx). Without
// the alias the operator had to manually map the number column before any
// row could match.
const SUPPLIER_NUMBER_HEADERS = [
  'numer dostawcy',
  'nr dostawcy',
  'nr tablicy',
  'nr',
  'numer',
  'symbol',
];
const LAT_HEADERS = ['lat', 'szerokość geograficzna', 'latitude'];
const LNG_HEADERS = ['lng', 'lon', 'długość geograficzna', 'longitude'];
const ADDRESS_HEADERS = ['adres', 'address'];
const CITY_HEADERS = ['miasto', 'city'];
const STATUS_HEADERS = ['status', 'dostępność', 'availability'];
const DATE_START_HEADERS = ['data od', 'od', 'data początkowa'];
const DATE_END_HEADERS = ['data do', 'do', 'data końcowa'];
/**
 * Header NAMES that describe a unit attribute, never a time period. Their
 * VALUES routinely pass every structural period-phrase check — e.g. a "typ"
 * column holding "billboard BB 12" (repeated, 2-20 distinct, non-status,
 * letter-first with internal whitespace) hijacked CS_10-11.xlsx (citisign,
 * CU-86canaqmw) into period_label_map, producing ZERO interpreted preview
 * rows for a file with no time information at all. A header-name blocklist
 * stays within the "Generic fallback scorer" requirement's no-value-NLP
 * rule — it never parses value meaning, only recognizes well-known
 * attribute column names.
 */
const NON_PERIOD_ATTRIBUTE_HEADERS = [
  'typ',
  'type',
  'rodzaj',
  'format',
  'materiał',
  'material',
  'oświetlenie',
  'lighting',
];

const ID_AND_TIME_CONFIDENCE = 0.5;
const PERIOD_LABEL_CONFIDENCE = 0.3;
const ID_ONLY_CONFIDENCE = 0.2;
const NO_MATCH_CONFIDENCE = 0.1;

function inferPolarityFromStatusColumn(
  statusHeader: string | undefined,
  sampleRows: Record<string, string>[],
): 'list_of_free' | 'list_of_busy' {
  if (statusHeader === undefined) {
    // An implicit-offer file with zero status column IS the free-list.
    return 'list_of_free';
  }
  const hasBusyToken = sampleRows.some((row) => {
    const raw = row[statusHeader];
    if (raw === undefined) return false;
    // isBusyToken (not a bare CANONICAL_BUSY_TOKENS lookup) so a status
    // column holding only compound busy tokens ('rez zr', 'zaj do
    // odwołania') infers list_of_busy the same way normalizeStatusToken
    // resolves those cells at import time.
    return isBusyToken(normalizeToken(raw));
  });
  return hasBusyToken ? 'list_of_busy' : 'list_of_free';
}

export interface DetectedColumns {
  grwIdHeader: string | undefined;
  supplierNumberHeader: string | undefined;
  latHeader: string | undefined;
  lngHeader: string | undefined;
  addressHeader: string | undefined;
  cityHeader: string | undefined;
  statusHeader: string | undefined;
  dateStartHeader: string | undefined;
  dateEndHeader: string | undefined;
}

/** Shared standard-column detection (id/GPS/address/status/date-range
 * headers). Exported for the month-matrix resolver, which previously left
 * `idColumns`/`addressColumns` at the skeleton's nulls — so every row of a
 * month-matrix file stayed unmatched until the operator manually picked the
 * supplier-number column (Bridge42_bb / Outdoor3miasto_pomorska,
 * CU-86canaqmw). */
export function detectColumns(headers: string[]): DetectedColumns {
  return {
    grwIdHeader: findHeaderAmong(headers, GRW_ID_HEADERS),
    supplierNumberHeader: findHeaderAmong(headers, SUPPLIER_NUMBER_HEADERS),
    latHeader: findHeaderAmong(headers, LAT_HEADERS),
    lngHeader: findHeaderAmong(headers, LNG_HEADERS),
    addressHeader: findHeaderAmong(headers, ADDRESS_HEADERS),
    cityHeader: findHeaderAmong(headers, CITY_HEADERS),
    statusHeader: findHeaderAmong(headers, STATUS_HEADERS),
    dateStartHeader: findHeaderAmong(headers, DATE_START_HEADERS),
    dateEndHeader: findHeaderAmong(headers, DATE_END_HEADERS),
  };
}

function computeConfidence(
  idColumn: string | undefined,
  hasTimeColumn: boolean,
  hasPeriodLabelColumn: boolean,
): number {
  if (idColumn !== undefined && hasTimeColumn) return ID_AND_TIME_CONFIDENCE;
  if (hasPeriodLabelColumn) return PERIOD_LABEL_CONFIDENCE;
  if (idColumn !== undefined) return ID_ONLY_CONFIDENCE;
  return NO_MATCH_CONFIDENCE;
}

const STATUS_VOCABULARY: ReadonlySet<string> = new Set<string>([
  ...CANONICAL_FREE_TOKENS,
  ...CANONICAL_BUSY_TOKENS,
]);

/**
 * Whether a normalized value reads as a credible free-text period phrase:
 * starts with a Unicode letter (never true for a numeric measurement, a
 * price, or an Excel-serial-date value — they start with a digit) and
 * contains internal whitespace (never true for a single categorical token —
 * a location name, a material code, an illumination type). A structural
 * value-SHAPE check only — it does NOT parse, translate, or keyword-match
 * the phrase's MEANING (no NLP, no Polish month-name matching), per the
 * "Generic fallback scorer" requirement. Deliberately narrow: a multi-word
 * location/address value (e.g. "Nowa Huta") also satisfies this check and
 * remains a known, out-of-scope false-positive risk (see proposal.md
 * Non-Goals for `availability-period-label-credibility`).
 */
function looksLikePeriodPhrase(value: string): boolean {
  return /^\p{L}/u.test(value) && /\s/.test(value);
}

/**
 * Whether one column's sampled non-blank values form a bounded set of period
 * phrases: between 2 and 20 distinct values, at least one of them repeated
 * across more than one row, none matching the known FREE/UNAVAILABLE
 * status-token vocabulary, AND at least one distinct value is a credible
 * period phrase (see `looksLikePeriodPhrase`). This is the positive signal
 * that distinguishes a period-label column from a status column — reused
 * both by `detectPeriodLabelColumn` and by the status-header release check
 * in `resolve` (a header whose NAME reads as a status column but whose
 * VALUES are period phrases, e.g. a "Dostępność" column holding "Cały rok
 * 2027"). Returns `false` for an empty sample so a status column with no
 * sampled values is never misclassified.
 *
 * The repetition requirement (`rawValues.length > distinctNormalized.size`)
 * rules out an id-like column (e.g. "Symbol", one unique value per row) that
 * would otherwise fall inside the same 2-20-distinct-value range purely
 * because the sample is small — a real period-label phrase is shared by
 * multiple units, an id/serial number never is.
 *
 * Only ONE distinct value needs to be a credible period phrase, not all of
 * them, because a real period-label column can legitimately mix a text
 * phrase with a raw non-phrase value (e.g. an Excel-serial-date cell) in the
 * same column — requiring every value to qualify would wrongly reject that
 * column too.
 */
function qualifiesAsPeriodLabelColumn(
  header: string,
  sampleRows: Record<string, string>[],
): boolean {
  const rawValues = sampleRows
    .map((row) => row[header])
    .filter((value): value is string => value !== undefined && value.trim() !== '');
  if (rawValues.length === 0) return false;

  const distinctNormalized = new Set(rawValues.map(normalizeToken));
  if (
    distinctNormalized.size < MIN_PERIOD_LABEL_DISTINCT_VALUES ||
    distinctNormalized.size > MAX_PERIOD_LABEL_DISTINCT_VALUES ||
    rawValues.length === distinctNormalized.size
  ) {
    return false;
  }

  const distinctValues = [...distinctNormalized];
  if (distinctValues.some((value) => STATUS_VOCABULARY.has(value))) return false;

  return distinctValues.some(looksLikePeriodPhrase);
}

/**
 * Detects a **period-label column**: a non-status, non-date column (not
 * already claimed by another detected role) whose values qualify as a
 * bounded period-phrase vocabulary (see `qualifiesAsPeriodLabelColumn`).
 * Returns the first such header in file order, or `undefined` when none
 * qualifies. Deliberately does NOT attempt to parse or translate the
 * distinct phrases — the operator fills `periodLabelMap` manually in the
 * preview screen.
 */
function detectPeriodLabelColumn(
  headers: string[],
  sampleRows: Record<string, string>[],
  claimedHeaders: ReadonlySet<string>,
): string | undefined {
  for (const header of headers) {
    if (claimedHeaders.has(header)) continue;
    // A well-known attribute header (typ/format/materiał...) is never a
    // period label, regardless of how phrase-shaped its values look — see
    // NON_PERIOD_ATTRIBUTE_HEADERS.
    if (NON_PERIOD_ATTRIBUTE_HEADERS.includes(normalizeHeaderForMatch(header))) continue;
    if (qualifiesAsPeriodLabelColumn(header, sampleRows)) return header;
  }

  return undefined;
}

interface TimeShapeFields {
  timeEncoding: AvailabilityTimeEncoding;
  dateRangeColumns: AvailabilityProfile['dateRangeColumns'];
  periodLabelColumn: string | null;
  periodLabelMap: AvailabilityProfile['periodLabelMap'];
}

/** Resolves the mutually-exclusive time-shape fields for one of the 3 branches this resolver can produce. */
function resolveTimeShapeFields(
  columns: DetectedColumns,
  hasDateRange: boolean,
  periodLabelColumn: string | undefined,
): TimeShapeFields {
  if (periodLabelColumn !== undefined) {
    return {
      timeEncoding: 'period_label_map',
      dateRangeColumns: null,
      periodLabelColumn,
      periodLabelMap: {},
    };
  }
  if (hasDateRange) {
    return {
      timeEncoding: 'date_range',
      dateRangeColumns: {
        startColumn: columns.dateStartHeader as string,
        endColumn: columns.dateEndHeader as string,
      },
      periodLabelColumn: null,
      periodLabelMap: null,
    };
  }
  return {
    timeEncoding: 'global',
    dateRangeColumns: null,
    periodLabelColumn: null,
    periodLabelMap: null,
  };
}

function buildProfile(
  columns: DetectedColumns,
  hasDateRange: boolean,
  periodLabelColumn: string | undefined,
  sampleRows: Record<string, string>[],
): AvailabilityProfile {
  const timeShape = resolveTimeShapeFields(columns, hasDateRange, periodLabelColumn);
  return {
    ...buildAvailabilityProfileSkeleton(),
    idColumns: {
      grwIdColumn: columns.grwIdHeader ?? null,
      supplierNumberColumn: columns.supplierNumberHeader ?? null,
    },
    gpsColumns: { latColumn: columns.latHeader ?? null, lngColumn: columns.lngHeader ?? null },
    addressColumns: {
      addressColumn: columns.addressHeader ?? null,
      cityColumn: columns.cityHeader ?? null,
    },
    periodColumns: [],
    globalPeriod: null,
    statusColumn: columns.statusHeader ?? null,
    polarity: inferPolarityFromStatusColumn(columns.statusHeader, sampleRows),
    ...timeShape,
  };
}

export const genericResolver: AvailabilityProfileResolver = {
  id: 'generic',
  resolve(headers, sampleRows): AvailabilityProfileResolveResult {
    const rawColumns = detectColumns(headers);
    const hasDateRange =
      rawColumns.dateStartHeader !== undefined && rawColumns.dateEndHeader !== undefined;
    // A header whose NAME matches the status vocabulary but whose VALUES are a
    // bounded set of non-status phrases (e.g. a "Dostępność" column holding
    // "Cały rok 2027") is a period-label column, not a status column.
    // Releasing it here lets the period-label detection below claim it instead
    // of the resolver mislabeling the file as a `global` status file. Gated on
    // `!hasDateRange`: when the file ALSO carries an explicit date-range pair,
    // the status-named column is the real per-row status for that date range
    // (e.g. `Blok A`/`Blok B`/`Blok C` unmapped tokens awaiting operator
    // mapping) — releasing it there would silently discard it and every row
    // would default to FREE instead of staying `unmapped`.
    const releaseStatusHeader =
      !hasDateRange &&
      rawColumns.statusHeader !== undefined &&
      qualifiesAsPeriodLabelColumn(rawColumns.statusHeader, sampleRows);
    const columns: DetectedColumns = releaseStatusHeader
      ? { ...rawColumns, statusHeader: undefined }
      : rawColumns;
    const idColumn = columns.grwIdHeader ?? columns.supplierNumberHeader;
    const hasTimeColumn = columns.statusHeader !== undefined || hasDateRange;

    // Only look for a period-label column when no status/date-range column
    // was already found — those take precedence (higher confidence).
    let periodLabelColumn: string | undefined;
    if (!hasTimeColumn && releaseStatusHeader) {
      // The status-named column we released above IS the period-label column;
      // prefer it over any earlier unclaimed column detectPeriodLabelColumn
      // might otherwise pick (e.g. an id-like "Symbol" column that also has a
      // handful of distinct values).
      periodLabelColumn = rawColumns.statusHeader;
    } else if (!hasTimeColumn) {
      const claimedHeaders = new Set(
        [
          columns.grwIdHeader,
          columns.supplierNumberHeader,
          columns.latHeader,
          columns.lngHeader,
          columns.addressHeader,
          columns.cityHeader,
          columns.statusHeader,
          columns.dateStartHeader,
          columns.dateEndHeader,
        ].filter((header): header is string => header !== undefined),
      );
      periodLabelColumn = detectPeriodLabelColumn(headers, sampleRows, claimedHeaders);
    }

    const profile = buildProfile(columns, hasDateRange, periodLabelColumn, sampleRows);
    const confidence = computeConfidence(idColumn, hasTimeColumn, periodLabelColumn !== undefined);

    return { profile, confidence };
  },
};
